import React, { useCallback, useEffect, useState } from "react";
import { DatePicker, Form, Input, Select } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { dateFormat, filterOptions, RANGE_OPTIONS, REGISTRATION_CATEGORY_LIST, STATUS_LIST } from "./variables";
import useStoreFilters from "customHooks/useStoreFilters";
import { FILTER_PAGE } from "utils/constants";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import moment from "moment/moment";

const { Option } = Select;
const { RangePicker } = DatePicker;

const useStyles = makeStyles({
  root: { width: "250px!important" },

  filtersContainer: {
    display: "flex",
    alignItems: "center",
    height: 60,
    overflow: "auto",

    "@media (max-width: 576px)": {
      height: "auto",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      gap: 10,
    },

    "& .ant-select-selection-overflow": {
      flexWrap: "nowrap",
    },
  },
  value: {
    width: "auto !important",
  },
  range: {
    width: "100px!important",
  },
});

const CustomerPriceGroupsFilter = ({ onFilter }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const { filters } = useStoreFilters(FILTER_PAGE.CUSTOMER_PRICE_GROUPS);
  const [activeFilter, setFilter] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);

  const handleFilterChange = useCallback(
    value => {
      setFilter(value);
      form.setFieldsValue({
        type: value,
        value: null,
      });
    },
    [form]
  );

  useEffect(() => {
    const { pageSize, ...dataToSet } = filters;
    if (filters?.activateDateFrom) {
      setFilter("activationDate");
      form.setFieldsValue({
        type: "activationDate",
        value: [moment(filters?.activateDateFrom), moment(filters?.activateDateTo)],
      });
      return;
    }
    if (filters?.deactivateDateFrom) {
      setFilter("deactivationDate");
      form.setFieldsValue({
        type: "deactivationDate",
        value: [moment(filters?.deactivateDateFrom), moment(filters?.deactivateDateTo)],
      });
      return;
    }
    if (!!Object.keys(dataToSet)[0]) {
      setFilter(Object.keys(dataToSet)[0]);
      form.setFieldsValue({
        value: dataToSet[Object.keys(dataToSet)[0]],
        type: Object.keys(dataToSet)[0],
      });
    }
  }, [filters]);

  const onFinish = useCallback(
    values => {
      const normalizedFilters = !!filters?.pageSize ? { pageSize: filters.pageSize } : {};
      if (!(values.value && !values.type)) {
        if (values?.type === "activationDate") {
          const filter = {};
          filter.activateDateFrom = customDateFormatWithoutTime(moment(values?.value[0]));
          filter.activateDateTo = customDateFormatWithoutTime(moment(values?.value[1]));
          onFilter(filter);
          return;
        }
        if (values?.type === "deactivationDate") {
          const filter = {};
          filter.deactivateDateFrom = customDateFormatWithoutTime(moment(values?.value[0]));
          filter.deactivateDateTo = customDateFormatWithoutTime(moment(values?.value[1]));
          onFilter(filter);
          return;
        }
        if (values?.type === "range") {
          const filter = {};
          if (selectedRule === "fromTo") {
            filter.rangeFromOperator = ">=";
            filter.rangeFrom = values.value1;
            filter.rangeToOperator = "<";
            filter.rangeTo = values.value2;
          }
          if (selectedRule === "from") {
            filter.rangeFromOperator = ">=";
            filter.rangeFrom = values.value1;
          }
          if (selectedRule === "to") {
            filter.rangeToOperator = "<";
            filter.rangeTo = values.value1;
          }
          onFilter(filter);
          return;
        }
        onFilter({ ...normalizedFilters, [values.type]: values.value?.trim() });
      }
    },
    [activeFilter, filters, selectedRule]
  );

  const onRuleChange = useCallback(value => {
    form.setFieldsValue({
      value1: null,
      value2: null,
    });
    setSelectedRule(value);
  }, []);

  return (
    <>
      <div className="custom-filter">
        <Form form={form} layout="inline" onFinish={onFinish}>
          <div className={classes.filtersContainer}>
            <Form.Item name="type">
              <Select
                tokenSeparators={[","]}
                size="default"
                placeholder="Select type"
                allowClear
                onChange={handleFilterChange}
              >
                {filterOptions.length > 0 &&
                  filterOptions.map(item => (
                    <Option key={item.value} value={item.value}>
                      {item.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            {!!activeFilter && <div className="filter-divider" />}
            {(activeFilter === "name" || activeFilter === "owner") && (
              <Form.Item name="value" className={classes.value}>
                <Input placeholder={"Enter value"} />
              </Form.Item>
            )}
            {activeFilter === "status" && (
              <Form.Item name="value">
                <Select showSearch size="default" placeholder="Select status" allowClear>
                  {STATUS_LIST.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {activeFilter === "registrationCategory" && (
              <Form.Item name="value">
                <Select showSearch size="default" placeholder="Select status" allowClear>
                  {REGISTRATION_CATEGORY_LIST.map(item => (
                    <Option key={`${item}-option`} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {(activeFilter === "activationDate" || activeFilter === "deactivationDate") && (
              <Form.Item
                name="value"
                rules={[{ required: true, message: "Please select date range" }]}
                className={classes.root}
              >
                <RangePicker inputReadOnly={true} format={dateFormat} size={"default"} />
              </Form.Item>
            )}
            {activeFilter === "range" && (
              <>
                <Form.Item name="rangeOptions">
                  <Select onChange={onRuleChange} size="default" placeholder="Select rule" allowClear>
                    {RANGE_OPTIONS.map(item => (
                      <Option key={`${item.value}-option`} value={item.value}>
                        {item.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedRule && (
                  <Form.Item className={classes.range} name="value1">
                    <Input placeholder={"Enter value"} />
                  </Form.Item>
                )}
                {selectedRule && selectedRule === "fromTo" && (
                  <>
                    {" "}
                    -
                    <Form.Item className={classes.range} name="value2">
                      <Input placeholder={"Enter value"} />
                    </Form.Item>
                  </>
                )}
              </>
            )}
          </div>

          <CustomButton type="primary" htmlType="submit" text="Filter" size="small" />
        </Form>
      </div>
    </>
  );
};

export default CustomerPriceGroupsFilter;
